/**
 * RatingTrendChart — full-width SVG trend chart for rating answers.
 * Combines the latest value with a trend line over recent periods.
 */
export type RatingTrendPoint = { periodStart: string; rating: number };

export function RatingTrendChart({
  data,
  max,
}: {
  data: RatingTrendPoint[];
  max: number;
}) {
  // Data comes newest-first from the server; reverse for chronological display.
  const points = [...data].reverse();
  if (points.length === 0) return null;

  const latest = points[points.length - 1];
  if (!latest) return null;

  // SVG viewBox; width stretches to container via preserveAspectRatio="none".
  const vbW = 100;
  const vbH = 32;

  const xPct = (i: number) =>
    points.length === 1 ? 50 : (i / (points.length - 1)) * 100;
  const yPct = (v: number) => ((max - v) / Math.max(max - 1, 1)) * 100;

  const linePath = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"} ${((xPct(i) / 100) * vbW).toFixed(2)} ${((yPct(p.rating) / 100) * vbH).toFixed(2)}`,
    )
    .join(" ");

  const areaPath =
    points.length >= 2 ? `${linePath} L ${vbW} ${vbH} L 0 ${vbH} Z` : "";

  const periodLabel =
    points.length === 1 ? "First check-in" : `${points.length} period trend`;

  const chartHeight = 72;

  return (
    <div class="mt-1">
      <div class="flex items-stretch gap-2">
        {/* Y-axis labels */}
        <div
          class="flex flex-col justify-between text-[10px] text-text-subtle flex-shrink-0 select-none"
          style={{ height: `${chartHeight}px` }}
        >
          <span class="leading-none">{max}</span>
          <span class="leading-none">1</span>
        </div>

        {/* Chart area */}
        <div
          class="relative flex-1 min-w-0"
          style={{ height: `${chartHeight}px` }}
        >
          {/* Min/max grid lines */}
          <div class="absolute inset-x-0 top-0 border-t border-dashed border-border-muted" />
          <div class="absolute inset-x-0 bottom-0 border-t border-dashed border-border-muted" />

          <svg
            class="absolute inset-0 w-full h-full"
            viewBox={`0 0 ${vbW} ${vbH}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {areaPath && (
              <path
                d={areaPath}
                fill="currentColor"
                class="text-[var(--brand-200)]"
                opacity="0.45"
              />
            )}
            {points.length >= 2 && (
              <path
                d={linePath}
                fill="none"
                stroke="currentColor"
                class="text-[var(--brand-500)]"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                vector-effect="non-scaling-stroke"
              />
            )}
          </svg>

          {/* Dots: rendered as HTML so they stay circular under non-uniform scaling */}
          {points.map((p, i) => {
            const isLast = i === points.length - 1;
            return (
              <div
                key={p.periodStart}
                class={
                  isLast
                    ? "absolute size-3 rounded-full bg-[var(--brand-600)] ring-2 ring-surface"
                    : "absolute size-2 rounded-full bg-surface ring-2 ring-[var(--brand-400)]"
                }
                style={{
                  left: `${xPct(i)}%`,
                  top: `${yPct(p.rating)}%`,
                  transform: "translate(-50%, -50%)",
                }}
                aria-hidden="true"
              />
            );
          })}
        </div>

        {/* Latest value */}
        <div
          class="flex flex-col items-start justify-center flex-shrink-0 leading-none"
          style={{ height: `${chartHeight}px` }}
        >
          <div class="flex items-baseline gap-0.5">
            <span class="text-2xl font-semibold text-[var(--brand-700)]">
              {latest.rating}
            </span>
            <span class="text-sm text-text-subtle">/{max}</span>
          </div>
          <span class="mt-1 text-[10px] uppercase tracking-wide text-text-subtle">
            Current
          </span>
        </div>
      </div>

      <div class="mt-1 text-xs text-text-subtle text-right">{periodLabel}</div>
    </div>
  );
}

// Backwards-compatible alias — older imports referenced `RatingSparkline`.
export const RatingSparkline = RatingTrendChart;
